import Box from '@mui/material/Box'
import React, { useCallback, useState } from 'react'

import LocationMapImageV2 from 'components/customer-details/content/location/LocationMapImageV2'
import LocationMapSkeleton from 'components/customer-details/content/location/LocationMapSkeleton'
import { MARKER_TYPE } from 'components/customer-details/content/location/settings'
import ContainerMarkerPill from 'components/inventory/containers/tabpanel/map/ContainerMarkerPill'
import { buildStaticMapUrl } from 'utils/staticMap'

import type { WorkOrderContainer } from './types'

const LocationMapSkeletonUntyped = LocationMapSkeleton as React.ElementType
const LocationMapImageV2Untyped = LocationMapImageV2 as React.ElementType

const THUMBNAIL_SIZE = 88
const THUMBNAIL_ZOOM = 19

type ContainerThumbnailProps = {
  container: WorkOrderContainer
}

// Static Maps <img> instead of a live map: N rows used to mount N interactive map instances
// (Maps JS + a mapConfig fetch each), delaying the whole section's perceived load.
const ContainerThumbnail: React.FC<ContainerThumbnailProps> = ({ container }) => {
  const [hasImageError, setHasImageError] = useState(false)

  const handleImageError = useCallback(() => setHasImageError(true), [])

  const latitude = parseFloat(String(container.latitude))
  const longitude = parseFloat(String(container.longitude))
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude)

  if (!hasValidCoordinates) {
    return (
      <Box sx={{ borderRadius: 1, flexShrink: 0, height: THUMBNAIL_SIZE, overflow: 'hidden', width: THUMBNAIL_SIZE }}>
        <LocationMapSkeletonUntyped height={`${THUMBNAIL_SIZE}px`} width={`${THUMBNAIL_SIZE}px`} />
      </Box>
    )
  }

  // Static Maps needs its API enabled on the browser key per environment — until it is, the
  // image 403s and the row degrades to the previous live-map thumb instead of losing the visual.
  if (hasImageError) {
    return (
      <Box sx={{ borderRadius: 1, flexShrink: 0, height: THUMBNAIL_SIZE, overflow: 'hidden', width: THUMBNAIL_SIZE }}>
        <LocationMapImageV2Untyped
          showPlaceholder
          containerData={container}
          containerId={`wo-container-thumb-${container.id}`}
          height={THUMBNAIL_SIZE}
          latitude={container.latitude}
          longitude={container.longitude}
          markerType={MARKER_TYPE.INVENTORY_CONTAINER}
          showInvalidAlert={false}
          width={THUMBNAIL_SIZE}
          zoom={THUMBNAIL_ZOOM}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ borderRadius: 1, flexShrink: 0, height: THUMBNAIL_SIZE, overflow: 'hidden', position: 'relative', width: THUMBNAIL_SIZE }}>
      <Box
        alt=""
        component="img"
        src={buildStaticMapUrl({ latitude, longitude, zoom: THUMBNAIL_ZOOM, width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE })}
        sx={{ display: 'block', height: '100%', objectFit: 'cover', width: '100%' }}
        onError={handleImageError}
      />
      {/* Same pill the live map shows — overlaid, scaled down to fit the 88px tile. */}
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          inset: 0,
          justifyContent: 'center',
          pointerEvents: 'none',
          position: 'absolute',
          transform: 'scale(0.75)',
        }}
      >
        <ContainerMarkerPill container={container} />
      </Box>
    </Box>
  )
}

export default ContainerThumbnail
